/**
 * Il disegno degli stati vuoti.
 *
 * **In linea e non `<img src="/empty.svg">`**: un'immagine caricata da fuori è
 * una scatola chiusa per il CSS, e i suoi colori restano quelli del file. Qui i
 * due teal fissi del disegno diventano il tema — l'accento pieno via
 * `currentColor`, la sua ombra via la classe `art-shade` (vedi globals.css).
 *
 * Il resto della tavolozza resta com'è: i neri sono il TRATTO del disegno e la
 * crema è un oggetto, non superfici dell'interfaccia. Ricolorarli col tema
 * trasformerebbe un'illustrazione in un pittogramma.
 *
 * ⚠️ Gli `id` dei pattern arrivano dall'esportazione originale e sono unici nel
 * file, non nella pagina: due copie di questo disegno visibili insieme
 * condividerebbero le stesse definizioni. Oggi non succede (uno stato vuoto per
 * schermata); se un giorno servissero due copie, gli id vanno resi unici.
 *
 * Convertito a macchina dal file originale, di proposito: trascrivere a mano 19
 * tracciati e 12 pattern è il modo migliore per introdurre in un disegno un
 * errore che nessuno noterà finché non lo guarda da vicino.
 */
export function EmptyArt({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      aria-hidden
      viewBox="0 0 180 126"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M141.368 109.437C141.368 109.437 126.82 112.809 121.799 102.899L141.368 109.437ZM160.937 108.382C158.547 107.769 142.608 101.846 142.608 101.846C137.647 99.6458 133.158 97.5643 129.057 95.5692C118.839 97.1723 107.775 97.4313 95.8673 96.3486C95.4764 97.4757 91.6779 115.989 89.2906 108.175C88.7406 112.94 83.9193 112.006 83.4605 109.925C82.7888 114.571 75.4796 113.497 74.6487 111.437C72.9098 116.013 64.6246 114.169 64.7112 110.779C64.2805 112.877 58.5254 112.76 58.5558 108.235C56.5453 110.053 53.161 107.104 54.3851 104.926C50.8323 107.293 50.4906 99.3471 52.6672 90.2722C51.1319 89.0285 49.8095 87.6261 48.6837 86.1023C43.7805 83.9952 39.0832 81.23 34.6317 77.8185C34.0863 67.9316 34.3438 59.0597 35.4087 51.2029C36.4291 37.4658 42.519 24.8417 52.157 15.5452C26.8824 27.8076 27.7788 51.5623 28.0761 54.3811C24.5584 32.1711 39.6449 18.0373 39.4928 17.8856C-1.84427 35.046 9.8416 79.914 9.83458 79.9373C3.84068 75.1887 0.0865967 69.901 0 70.132C17.8272 110.58 50.6825 108.471 50.5936 108.52C30.4446 111.045 16.7974 99.0998 16.4815 98.7217C35.7457 127.927 76.9236 125.783 76.9236 125.783H178.457C178.457 125.783 170.5 114.988 168.382 112.198C166.266 109.402 163.328 108.994 160.937 108.382ZM167.7 116.281C167.7 116.281 159.401 113.42 155.303 112.195C162.271 112.473 165.65 113.42 165.65 113.42L167.7 116.281Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M47.51 83.9345C43.5312 81.9721 41.6097 82.1611 38.3331 79.6013C37.4296 78.9736 36.6854 78.7659 35.8007 78.0892C35.5268 68.2513 34.3496 59.0574 35.4145 51.2006C35.5034 49.9942 35.6322 48.7971 35.7983 47.6094C35.3115 49.9475 34.9745 52.3323 34.7966 54.7521C33.7317 62.6089 33.4719 71.4808 34.0173 81.3701C39.4729 85.5516 45.3029 88.7648 51.4232 90.9793C51.7556 91.1006 52.0902 91.2173 52.4249 91.3316C52.5022 90.9793 52.5841 90.6246 52.6683 90.2699C52.8579 89.4765 53.0686 88.6715 53.2979 87.8664C51.7415 87.3414 50.8732 86.2097 49.3543 85.5563C49.2606 85.7733 47.5568 83.7339 47.51 83.9345Z"
        className="art-shade"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M54.1235 85.379C48.4502 83.2112 44.6985 78.2665 40.0176 74.5796C39.5635 65.2598 39.4301 58.7051 40.4412 51.2916C41.8782 32.6075 50.7696 17.93 67.2698 9.80248C83.7723 1.675 102.145 2.23971 117.049 12.8733C131.951 23.5093 139.707 40.5997 137.961 59.2581C136.959 74.6753 134.027 83.7502 126.319 90.2442C117.016 92.048 127.628 88.6248 116.763 87.8594C117.678 85.2833 97.0942 85.3183 94.2061 88.3985C91.9031 90.8556 90.1711 94.5145 89.5111 97.73C88.6498 101.937 88.4954 106.695 87.3134 102.826C86.3773 109.012 82.8221 107.367 82.4219 105.552C81.8368 109.598 74.9956 109.409 74.2724 107.615C72.7558 111.6 68.1334 109.239 66.4904 105.099C65.2898 107.673 61.1495 106.891 61.1776 102.95C59.5369 104.756 56.918 103.68 57.4212 99.4614C54.2218 104.681 53.976 93.5438 57.9782 85.3719C57.255 85.1363 54.8631 85.6636 54.1235 85.379ZM34.6345 77.8185C40.4623 82.2848 46.7183 85.6473 53.2973 87.8688C50.456 97.8747 50.5215 107.498 54.3856 104.924C53.1616 107.104 56.5459 110.053 58.5563 108.233C58.5259 112.758 64.281 112.874 64.7117 110.776C64.6227 114.169 72.908 116.01 74.6493 111.435C75.4801 113.495 82.7893 114.568 83.461 109.922C83.9198 112.004 88.7411 112.94 89.2911 108.175C91.6784 115.989 95.4769 97.4757 95.8678 96.3486C108.86 97.5293 120.85 97.1116 131.834 95.0979C133.088 94.0735 134.144 92.8297 135.349 91.705C141.762 85.7336 145.523 77.1278 145.436 65.7591C145.422 63.9904 145.815 62.1843 145.932 60.3035C149.085 45.3249 142.251 26.1204 129.832 14.5698C127.927 12.7987 124.597 11.4452 122.423 9.93316C106.079 -1.42618 84.8794 -3.17161 66.8883 5.3619C48.8972 13.8954 36.8814 31.3964 35.4116 51.2053C34.349 59.0574 34.0892 67.9316 34.6345 77.8185Z"
        fill="#1A1A1A"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M37.8016 76.0848C42.4802 79.7717 48.4507 83.2112 54.1239 85.379C54.8635 85.6614 55.5984 85.9227 56.324 86.1607C52.3218 94.3326 51.9777 107.664 55.1771 102.444C54.6739 106.663 57.4965 108.301 59.1372 106.492C59.1091 110.434 63.8415 111.194 65.0421 108.62C66.6851 112.76 72.9692 112.867 74.4835 108.882C75.2067 110.676 82.9372 111.185 83.5223 107.139C83.9226 108.952 88.2898 110.669 89.226 104.483C90.408 108.352 91.5875 104.523 92.4488 100.316C93.1065 97.1001 93.1556 92.5848 95.4586 90.1276C97.8365 87.5912 102.517 86.0464 110.164 87.6985C102.742 87.4885 98.0753 89.1966 96.5984 93.3572C107.463 94.1226 117.333 93.7469 126.636 91.9408C134.343 85.4444 137.07 73.336 138.071 57.9188C139.817 39.2603 133.32 22.3264 118.418 11.6904C103.517 1.05444 84.1262 -0.492655 67.6237 7.63482C51.1211 15.7623 40.0461 32.3159 38.6091 51C37.598 58.4111 37.3476 66.7673 37.8016 76.0848Z"
        fill="#FFF5DB"
      />
      <rect
        x="52.082"
        y="5.17896"
        width="41.5841"
        height="51.8646"
        fill="url(#pattern0_3060_45866)"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M51.7425 42.0863C65.3709 43.0103 66.2509 43.645 63.5384 56.0754C58.1998 80.5349 42.9681 66.6904 45.1354 50.7318C45.9826 44.5061 45.6667 41.6732 51.7425 42.0863Z"
        fill="#1A1A1A"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M83.9824 59.0085C85.2743 49.0936 86.8213 46.1207 97.9338 48.1298C115.939 51.385 115.162 58.0004 121.65 60.6162C120.086 77.3892 106.418 79.4823 93.0001 77.2048C83.8349 75.6508 82.2738 72.1109 83.9824 59.0085Z"
        fill="#1A1A1A"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M61.2793 83.2763C64.0551 82.5553 66.5851 84.3707 68.8694 88.7203C67.657 83.5353 66.3744 71.448 61.2793 83.2763Z"
        fill="#1A1A1A"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M56.9062 37.7225C61.1612 34.6424 65.9053 34.8897 71.1385 38.4646C65.4044 33.5573 61.8914 31.3849 56.9062 37.7225Z"
        fill="#1A1A1A"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M107.504 45.005C102.757 41.9738 90.6593 37.8646 84.2324 39.0873C90.2497 36.3782 97.25 34.6351 107.504 45.005Z"
        fill="#1A1A1A"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M45.898 60.4297C42.3709 66.562 46.0338 70.7809 53.2728 72.5054C44.8191 73.4528 38.3313 66.2774 45.898 60.4297Z"
        fill="#1A1A1A"
      />
      <rect
        x="37.5625"
        y="40.7271"
        width="22.9249"
        height="45.4341"
        fill="url(#pattern1_3060_45866)"
      />
      <rect
        x="121.578"
        y="32.9219"
        width="18.3528"
        height="59.8623"
        fill="url(#pattern2_3060_45866)"
      />
      <rect
        x="80.3105"
        y="2.46777"
        width="57.2093"
        height="91.3753"
        fill="url(#pattern3_3060_45866)"
      />
      <rect
        x="96.377"
        y="87.668"
        width="30.2583"
        height="6.1743"
        fill="url(#pattern4_3060_45866)"
      />
      <rect
        x="87.5547"
        y="89.8157"
        width="7.90263"
        height="16.2706"
        fill="url(#pattern5_3060_45866)"
      />
      <rect
        x="81.9023"
        y="94.0186"
        width="7.32315"
        height="14.8027"
        fill="url(#pattern6_3060_45866)"
      />
      <rect
        x="74.4434"
        y="93.4802"
        width="9.09675"
        height="16.7465"
        fill="url(#pattern7_3060_45866)"
      />
      <rect
        x="65.041"
        y="92.6658"
        width="10.1076"
        height="19.1354"
        fill="url(#pattern8_3060_45866)"
      />
      <rect
        x="59.1348"
        y="92.2229"
        width="7.89176"
        height="17.8989"
        fill="url(#pattern9_3060_45866)"
      />
      <rect
        x="55.1172"
        y="90.845"
        width="6.19876"
        height="16.3771"
        fill="url(#pattern10_3060_45866)"
      />
      <rect
        x="53.0215"
        y="86.1621"
        width="4.83132"
        height="17.4612"
        fill="url(#pattern11_3060_45866)"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M146.941 60.9171C147.697 62.3265 147.308 63.6379 147.87 65.031C152.626 76.8314 150.898 87.4743 150.898 87.4743C150.669 79.7389 149.106 73.7302 147.221 69.2406C146.655 67.8872 146.304 69.7353 145.707 68.6526C145.253 72.4305 145.157 73.1492 144.254 76.3484C142.454 82.7187 138.929 87.647 135.341 91.7142C142.271 94.8318 156.242 100.031 163.605 95.2378C190.447 76.0333 174.771 31.317 174.464 30.4746C175.899 38.6465 174.006 49.5811 173.849 49.4738C160.735 5.95919 126.272 4.75513 126.516 4.93714C160.822 21.7358 155.247 51.6369 155.202 51.9239C153.517 20.184 130.288 10.4348 118.68 7.53896C119.955 8.28101 121.626 9.00438 122.85 9.85376C134.517 17.9626 143.198 29.1702 146.112 42.639C147.28 48.0363 148.389 54.0473 146.781 58.915C146.765 59.09 146.952 60.7444 146.941 60.9171Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M145.456 65.5913C144.098 76.8946 140.726 85.6031 135.338 91.7145C135.53 91.8008 135.726 91.8895 135.93 91.9782C141.528 86.7745 145.301 79.2211 147.251 69.3202C146.673 67.9364 146.067 66.695 145.456 65.5913Z"
        className="art-shade"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M122.425 9.93075C138.768 21.2901 147.741 40.5202 145.935 60.3011C145.923 60.4737 145.914 60.6464 145.902 60.8191C146.688 62.2892 147.365 63.7569 147.94 65.206C148.24 63.0429 148.465 60.7841 148.61 58.4226C150.248 40.4992 143.035 23.0308 129.509 11.4568C125.5 9.51072 121.697 8.29264 118.678 7.54126C119.951 8.2833 121.201 9.07903 122.425 9.93075Z"
        className="art-shade"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M123.052 92.5616C122.753 92.4076 122.456 92.2536 122.161 92.0996C120.139 91.0472 118.222 90.0111 116.394 88.9914C114.674 88.03 113.033 87.0779 111.458 86.1329C111.236 85.9999 111.016 85.8669 110.796 85.7338C106.496 83.125 102.705 80.5535 99.1775 77.9121C93.5932 73.7281 88.6689 69.3646 83.445 64.3849C82.7358 63.7082 82.0196 63.0199 81.2941 62.3198C82.0594 75.4573 87.1991 84.4691 91.6389 89.8991C91.887 90.2024 92.1351 90.4965 92.3785 90.7788C92.9425 91.4322 93.4902 92.0249 94.0098 92.5592C96.5 95.1191 98.3537 96.3325 98.3537 96.3325H93.083C92.8559 97.7256 92.6968 99.114 92.4487 100.318C91.5874 104.525 90.4078 108.354 89.2259 104.486C88.2897 110.672 83.9224 108.954 83.5222 107.141C82.9371 111.187 75.2066 110.681 74.4834 108.884C72.9668 112.87 66.685 112.765 65.042 108.623C63.8414 111.197 59.109 110.436 59.137 106.495C57.4987 108.301 54.6738 106.665 55.177 102.446C52.6212 106.618 52.3263 98.9413 54.3555 91.5162C53.7657 91.1218 52.0665 92.7319 51.5306 92.2979C41.8247 97.3195 43.7018 105.809 47.2546 103.445C47.828 104.43 55.1489 112.006 55.7855 111.101C55.7551 115.626 58.3389 118.113 58.7696 116.015C58.683 119.408 70.6333 122.236 72.3723 117.663C77.1726 129.566 96.8558 108.056 98.4473 103.464C110.353 104.546 122.21 99.891 129.06 95.5718C126.963 94.5497 124.967 93.551 123.062 92.5686C123.057 92.5639 123.055 92.5616 123.052 92.5616Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M89.2261 104.483C88.2899 110.669 83.9226 108.952 83.5224 107.139C82.9373 111.185 75.2068 110.679 74.4836 108.882C72.967 112.867 66.6852 112.762 65.0422 108.62C63.8415 111.194 59.1091 110.433 59.1372 106.492C57.4989 108.298 54.674 106.663 55.1772 102.444C52.6214 106.616 52.3265 98.9388 54.3557 91.5137C54.3931 90.3587 54.351 89.619 54.0467 89.3903C53.794 89.2013 53.0731 89.738 52.6705 90.2677C50.6531 98.6752 50.7982 106.114 53.6582 105.279C53.8852 105.214 53.6559 105.75 53.918 105.575C52.9069 107.862 56.537 110.634 58.3602 108.725C58.3298 113.25 64.1481 113.479 64.5764 111.381C64.4875 114.774 72.9599 116.54 74.6989 111.964C75.5298 114.025 82.8882 115.084 83.5599 110.438C84.0186 112.52 88.8633 113.899 89.4157 109.134C89.4485 109.241 89.4812 109.344 89.514 109.439C90.8574 113.376 92.8679 108.821 94.223 104.376C95.2341 101.062 96.1141 97.2214 96.3294 96.4957C96.3341 96.4794 96.0415 94.9929 96.0462 94.9789L94.0404 95.1189L93.0832 96.323C92.8562 97.7161 92.697 99.1045 92.4489 100.309C91.5876 104.523 90.408 108.354 89.2261 104.483Z"
        fill="#1A1A1A"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M97.2908 96.33L95.8771 96.2483C95.8678 96.3253 95.8608 96.372 95.8584 96.3766C95.6454 97.1024 94.8614 100.67 93.8526 103.986C92.4975 108.431 90.7352 112.417 89.3918 108.48C89.359 108.382 89.3262 108.282 89.2935 108.175C88.7435 112.94 83.9221 112.006 83.4634 109.923C82.7917 114.568 75.4824 113.495 74.6516 111.435C72.9126 116.011 64.6274 114.167 64.714 110.777C64.2834 112.874 58.5282 112.758 58.5586 108.233C56.5482 110.051 53.1639 107.101 54.388 104.924C53.0024 105.846 52.1037 105.2 51.6543 103.529C51.652 107.477 52.6396 109.705 54.7577 108.294C54.2428 109.208 54.5354 110.259 55.1977 111.04C55.3709 111.246 55.5722 111.432 55.7899 111.593C56.7307 112.291 58.004 112.501 58.9659 111.631C58.9355 116.195 64.7421 116.314 65.1751 114.197C65.0862 117.618 73.4439 119.478 75.1992 114.862C76.0371 116.942 83.4119 118.024 84.0883 113.336C84.5517 115.437 89.4151 116.379 89.9698 111.572C90.2039 112.338 90.452 112.853 90.7071 113.159C93.2956 116.232 96.6823 97.7604 97.2908 96.33Z"
        className="art-shade"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M91.6379 89.8967C88.3355 85.8575 84.6446 79.8325 82.6552 71.6023C81.8501 70.8509 81.038 70.0879 80.2212 69.3085C79.5097 68.6318 78.7959 67.9434 78.0703 67.2434C78.625 76.7756 81.8244 86.3335 87.6989 93.9243C87.94 94.2346 88.1764 94.5333 88.4151 94.8227C88.6632 95.126 88.9113 95.42 89.1547 95.7024C89.3817 95.9637 89.6041 96.2157 89.8241 96.4584C90.1517 96.8201 90.4747 97.1608 90.786 97.4828C91.4577 98.1735 92.0849 98.7662 92.6396 99.2656C92.7918 98.3345 92.9205 97.3358 93.082 96.3324C93.2926 95.0304 93.5618 93.7259 94.0088 92.5592C93.4892 92.0248 92.9415 91.4321 92.3775 90.7788C92.1317 90.4941 91.886 90.2001 91.6379 89.8967Z"
        fill="#A39D8E"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M85.9854 86.9005C87.6495 78.0193 78.6013 82.0096 80.6094 73.6021C76.0549 83.7737 85.2599 79.3214 85.9854 86.9005Z"
        fill="#1A1A1A"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M69.3712 78.9201C68.2291 75.4059 66.1297 75.0535 63.0801 77.8607C66.6446 73.8238 68.6433 72.16 69.3712 78.9201Z"
        fill="#615E56"
      />
      <defs>
        <pattern
          id="pattern0_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image0_3060_45866" />
        </pattern>
        <pattern
          id="pattern1_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image0_3060_45866" />
        </pattern>
        <pattern
          id="pattern2_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image0_3060_45866" />
        </pattern>
        <pattern
          id="pattern3_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image1_3060_45866" />
        </pattern>
        <pattern
          id="pattern4_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image0_3060_45866" />
        </pattern>
        <pattern
          id="pattern5_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image0_3060_45866" />
        </pattern>
        <pattern
          id="pattern6_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image2_3060_45866" />
        </pattern>
        <pattern
          id="pattern7_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image1_3060_45866" />
        </pattern>
        <pattern
          id="pattern8_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image3_3060_45866" />
        </pattern>
        <pattern
          id="pattern9_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image3_3060_45866" />
        </pattern>
        <pattern
          id="pattern10_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image4_3060_45866" />
        </pattern>
        <pattern
          id="pattern11_3060_45866"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image5_3060_45866" />
        </pattern>
        <image
          id="image0_3060_45866"
          preserveAspectRatio="none"
          xlinkHref="data:image/jpeg;base64,/9j/4AAQSkZJRgABAgEASQBJAAD/7AARRHVja3kAAQAEAAAAHgAA/+4AIUFkb2JlAGTAAAAAAQMA"
        />
        <image
          id="image1_3060_45866"
          preserveAspectRatio="none"
          xlinkHref="data:image/jpeg;base64,/9j/4AAQSkZJRgABAgEASABIAAD/7AARRHVja3kAAQAEAAAAHgAA/+4AIUFkb2JlAGTAAAAAAQMA"
        />
        <image
          id="image2_3060_45866"
          preserveAspectRatio="none"
          xlinkHref="data:image/jpeg;base64,/9j/4AAQSkZJRgABAgEASgBKAAD/7AARRHVja3kAAQAEAAAAHgAA/+4AIUFkb2JlAGTAAAAAAQMA"
        />
        <image
          id="image3_3060_45866"
          preserveAspectRatio="none"
          xlinkHref="data:image/jpeg;base64,/9j/4AAQSkZJRgABAgEASwBLAAD/7AARRHVja3kAAQAEAAAAHgAA/+4AIUFkb2JlAGTAAAAAAQMA"
        />
        <image
          id="image4_3060_45866"
          preserveAspectRatio="none"
          xlinkHref="data:image/jpeg;base64,/9j/4AAQSkZJRgABAgEATABMAAD/7AARRHVja3kAAQAEAAAAHgAA/+4AIUFkb2JlAGTAAAAAAQMA"
        />
        <image
          id="image5_3060_45866"
          preserveAspectRatio="none"
          xlinkHref="data:image/jpeg;base64,/9j/4AAQSkZJRgABAgEATQBNAAD/7AARRHVja3kAAQAEAAAAHgAA/+4AIUFkb2JlAGTAAAAAAQMA"
        />
      </defs>
    </svg>
  );
}
